import useModals from 'src/Hooks/useModals'
import { cloudKeysApi } from 'src/Libs/installation-rights-management/api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Installation } from 'src/Libs/installations/models/installation.model'

interface UseCloudKeysHook {
  /**
   * Opens a modal dialog which initiates and handles the process to create a new CloudKey
   * @param installation
   */
  createNewKeyDialog: (installation: Installation) => Promise<boolean>

  /**
   * Removes an existing CloudKey for a single Installation. Combined to a confirmation dialog.
   *
   * @param installationId
   * @param cloudKeyId
   */
  removeCloudKey: (installationId: number, cloudKeyId: number) => Promise<boolean>

  /**
   * Updates the description of a single CloudKey of an Installation.
   *
   * @param installationId
   * @param cloudKeyId
   * @param description
   */
  updateCloudKey: (installationId: number, cloudKeyId: number, description: string) => Promise<boolean>
}

/**
 * This hook provides utils to support the ui components in creating and changing a CloudKey
 *
 * It combines the workflows of ui/ux behaviours, with the direct api data access to read and change the meta-data of
 * an CloudKey. Or create a new one.
 *
 */
const useCloudKeys = (): UseCloudKeysHook => {
  const { openSimpleTextDialog } = useModals()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()

  const createNewCloudKey = (installation: Installation, description: string, onSuccess: () => void, onFailure: () => void): void => {
    cloudKeysApi.createNew(installation.id, description, authFetch).then(result => {
      if (result.hasErrors) onFailure()
      onSuccess()
    })
  }

  const removeCloudKey = async (installationId: number, cloudKeyId: number): Promise<boolean> => {
    const result = await cloudKeysApi.removeOne(installationId, cloudKeyId, authFetch)
    return result.hasErrors
  }

  const updateCloudKey = async (installationId: number, cloudKeyId: number, description: string): Promise<boolean> => {
    const result = await cloudKeysApi.updateOne(installationId, cloudKeyId, description, authFetch)
    return result.hasErrors
  }

  /**
   * @param installation
   */
  const createNewKeyDialog = async (installation: Installation): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const onSuccess = (): void => {
        enqueueSuccessStack(t('installation.access-and-invitations.create-cloud-key.success'))
        resolve(true)
      }
      const onFailure = (): void => {
        enqueueErrorStack(t('installation.access-and-invitations.create-cloud-key.failure'))
        reject()
      }

      const onConfirm = (name: string, close: () => void): void => {
        createNewCloudKey(installation, name, onSuccess, onFailure)
        close()
      }

      openSimpleTextDialog({
        onConfirm: onConfirm,
        confirmTitle: t('installation.access-and-invitations.dialogs.create-cloud-key-confirm.title'),
        confirmText: t('installation.access-and-invitations.dialogs.create-cloud-key-confirm.description'),
        title: t('installation.access-and-invitations.dialogs.create-cloud-key.title'),
      })
    })
  }

  return { createNewKeyDialog, removeCloudKey, updateCloudKey }
}

export default useCloudKeys
